import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { useLeadTracking } from '../../../../../hooks/useConvertLeadTracking';

import { pushToDataLayer } from '../../../../../services/gtmService';

import { RESERVATION_AD_ID_LOCAL_STORAGE_KEY } from './ReservationTracking.constants';

import {
  TRUSTAP_CODE_CANCELLED,
  TRUSTAP_CODE_QUERY_KEY,
  TRUSTAP_STATUS_INTERNAL_SERVER_ERROR,
  TRUSTAP_STATUS_OK,
  TRUSTAP_STATUS_QUERY_KEY,
} from '../../constants/trustap';

import type { ReservationTrackingArgs } from './ReservationTracking.typed';
import { RESERVATION_STATUS } from '../../types';
import { PAGE } from '../../../../../helpers/pages';
import { CONVERT_GOAL_KEYS } from '../../../../../helpers/convertGoalKeys';

const useReservationTracking = (args: ReservationTrackingArgs) => {
  const { adId, reservationStatus } = args;
  const { query } = useRouter();
  const { trackLead } = useLeadTracking({ page: PAGE.AD_DETAILS });

  useEffect(() => {
    const reservedAdId = window.localStorage.getItem(
      RESERVATION_AD_ID_LOCAL_STORAGE_KEY,
    );
    const parsedAdId = reservedAdId && parseInt(reservedAdId);

    if (reservedAdId) {
      /**
       * Determine if the ad from which the checkout started is the same as the ad the user is currently viewing
       */
      if (parsedAdId === adId) {
        if (
          query[TRUSTAP_STATUS_QUERY_KEY] === TRUSTAP_STATUS_OK &&
          query[TRUSTAP_CODE_QUERY_KEY] !== TRUSTAP_CODE_CANCELLED
        ) {
          pushToDataLayer({
            event: 'reservation_disabled',
          });
          trackLead(CONVERT_GOAL_KEYS.PRO_LEAD_GENERATED);
        } else if (
          (query[TRUSTAP_STATUS_QUERY_KEY] === TRUSTAP_STATUS_OK &&
            query[TRUSTAP_CODE_QUERY_KEY] === TRUSTAP_CODE_CANCELLED) ||
          query[TRUSTAP_STATUS_QUERY_KEY] ===
            TRUSTAP_STATUS_INTERNAL_SERVER_ERROR
        ) {
          pushToDataLayer({
            event: 'reservation_enabled',
          });
        } else {
          /**
           * In the event that the user does not return to ad details from the checkout, we use the reservation status of the ad, if any,
           * rather than Trustap queries.
           *
           * Those queries will only be available based on certain actions during the checkout process, and upon returning directly to ad details.
           */
          pushToDataLayer({
            event: `reservation_${
              reservationStatus === RESERVATION_STATUS.ENABLED
                ? 'enabled'
                : 'disabled'
            }`,
          });
        }
      }
      window.localStorage.removeItem(RESERVATION_AD_ID_LOCAL_STORAGE_KEY);
    }
  }, [query, reservationStatus, adId, trackLead]);
};

export { useReservationTracking };
